import { DescriptionSharp, ShoppingCart } from "@mui/icons-material";
import { useTheme } from "@mui/material";
import { ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { AnyAction, bindActionCreators, Dispatch } from "redux";
import i18n from "../../i18n";
import { thunkFindReceiptDetailsForInteraction } from "../../store/actions/CustomerCareActions";
import { IStore } from "../../store/IStore";
import { formatCustomerInteractionType } from "../../store/models/bonusRule/CustomerInteractionType";
import { CustomerInteractionDto, InteractionType } from "../../store/models/customer/CustomerInteractionDto";
import { getCustomerStatusText } from "../../store/models/customer/CustomerStatusDto";
import { CustomerHistoryDto, EntityType, EventType, TransferType } from "../../store/models/history/HistoryDto";
import { OperationalUnitDto } from "../../store/models/operationalUnit/OperationalUnitDto";
import { ReceiptStatus } from "../../store/models/receipt/ReceiptDto";
import { DateTag } from "../atoms/DateTag";
import ImsTable, { ImsHeadCell, ImsTableRow } from "../atoms/ImsEmbeddedTable";
import { formatEuro, formatGermanDate } from "../atoms/Utils";
import { formatRewards } from "./KeyFigures";
import NoteDialog, { hasNoteDialog } from "./NoteDialog";
import ReceiptDialog from "./ReceiptDialog";

interface HistoryFeedTableProps {
  maxTableSize?: number;
  showBranchColumn?: boolean;
}

interface GroupedHistory {
  action?: CustomerHistoryDto;
  reversal?: CustomerHistoryDto;
  bonus?: CustomerHistoryDto;
  customer?: CustomerHistoryDto;
}

const HistoryFeedTable: React.FC<ThunkProps & HistoryFeedTableProps> = (props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { customerHistory, customerNumber, operationalUnits, maxTableSize, showBranchColumn, receiptDetails } = props;
  const [receiptDialogVisible, setReceiptDialogVisible] = useState(false);
  const [receiptInteraction, setReceiptInteraction] = useState({} as CustomerInteractionDto);
  const [noteDialogVisible, setNoteDialogVisible] = useState(false);
  const [note, setNote] = useState({} as CustomerHistoryDto);
  const operationalUnitByExternal = useMemo(
    () => new Map(operationalUnits.map((unit) => [unit.externalUnitNumber, unit])),
    [operationalUnits]
  );
  if (!customerNumber) return null;
  if (!customerHistory) return null;

  if (!customerHistory.length) return <p>keine Interaktionsdaten gefunden</p>;

  const branchCells = (row: CustomerHistoryDto): ReactNode[] => {
    const unit = operationalUnitByExternal.get(row.interaction?.externalUnitNumber ?? "");
    return [<>{unit?.externalUnitNumber ?? ""}</>, <>{unit?.unitName ?? ""}</>];
  };

  const headCells: ImsHeadCell[] = [
    { title: t("customerCareDetailsPage.historyFeedHeadingDate"), align: "left" },
    { title: t("customerCareDetailsPage.historyFeedHeadingInteraction"), align: "left", headAlign: "center" },
    ...(showBranchColumn
      ? ([
          {
            title: t("customerCareDetailsPage.historyFeedHeadingBranchNumber"),
            align: "left",
            headAlign: "center",
          },
          {
            title: t("customerCareDetailsPage.historyFeedHeadingBranchName"),
            align: "left",
            headAlign: "center",
          },
        ] as ImsHeadCell[])
      : []),
    {
      title: (
        <span style={{ paddingRight: theme.spacing(2) }}>{t("customerCareDetailsPage.historyFeedHeadingAmount")}</span>
      ),
      align: "right",
    },
    { title: t("customerCareDetailsPage.historyFeedHeadingPoints"), align: "right" },
  ];
  const groupedHistory = groupHistory(customerHistory);

  const formatAction = (action: CustomerHistoryDto, operationalUnits: OperationalUnitDto[]) => (
    <>
      <b>
        {formatEntityType(action, operationalUnits, showBranchColumn)} {formatEventType(action)}{" "}
        {formatEventDetails(action)}
        {}
      </b>
      {(action.interaction?.interactionType === InteractionType.PAYMENT_INTERACTION ||
        action.interaction?.interactionType === InteractionType.REVERSAL_INTERACTION) && (
        <ShoppingCart
          style={{
            color: theme.palette.primary.main,
            fontSize: 21,
            marginBottom: -5,
          }}
          onClick={() => {
            props.thunkFindReceiptDetailsForInteraction(action.interaction!.interactionNumber);
            setReceiptDialogVisible(true);
            setReceiptInteraction(action.interaction!);
          }}
        />
      )}
      {hasNoteDialog(action) && (
        <DescriptionSharp
          style={{
            color: theme.palette.primary.main,
            fontSize: 21,
            marginBottom: -5,
          }}
          onClick={() => {
            setNoteDialogVisible(true);
            setNote(action);
          }}
        />
      )}
      <br />
    </>
  );

  const receiptRows: ImsTableRow[] = customerHistory
    .filter((row) => row.entityType === EntityType.RECEIPT)
    .slice(0, maxTableSize)
    .map((row) => {
      // Für RECEIPT COMPLETED: Bonuspunkte und Amount direkt aus receipt
      let amountNode = <div>{formatAmount(row)}</div>;
      let bonusNode = <div>{formatRewards(row.interaction?.bonusPoints, row.interaction?.stampCard)}</div>;
      if (row.eventType === EventType.UPDATE && row.receipt?.status === ReceiptStatus.COMPLETED) {
        amountNode = <div>{formatEuro(row.receipt?.totalAmount)}</div>;
        bonusNode = <div>{formatRewards(row.receipt?.bonusPoints)}</div>;
      }
      return {
        key: row.historyNumber,
        nodes: [
          <DateTag date={row.eventTimestamp} />,
          <>{formatAction(row, operationalUnits)}</>,
          ...(showBranchColumn ? branchCells(row) : []),
          amountNode,
          bonusNode,
        ],
      };
    });

  const groupedRows: ImsTableRow[] = groupedHistory
    .filter(
      (group) =>
        group.action?.entityType !== EntityType.RECEIPT &&
        group.reversal?.entityType !== EntityType.RECEIPT &&
        group.bonus?.entityType !== EntityType.RECEIPT &&
        group.customer?.entityType !== EntityType.RECEIPT
    )
    .slice(0, maxTableSize)
    .map((row) => {
      const primary = (row.action || row.reversal || row.bonus || row.customer)!;
      return {
        key: primary.historyNumber,
        nodes: [
          <DateTag date={primary.eventTimestamp} />,
          <>
            {row.action && formatAction(row.action, operationalUnits)}
            {row.reversal && formatAction(row.reversal, operationalUnits)}
            {row.bonus && (
              <>
                {formatEntityType(row.bonus, operationalUnits, showBranchColumn)} {formatEventType(row.bonus)}
                <br />
              </>
            )}
            {row.customer && formatAction(row.customer, operationalUnits)}
          </>,
          ...(showBranchColumn ? branchCells(primary) : []),
          <div>
            {formatAmount(row.action)}
            {formatAmount(row.reversal)}
            {formatAmount(row.bonus)}
            {formatAmount(row.customer)}
          </div>,
          <GroupedRewards row={row} />,
        ],
      };
    });

  const rows: ImsTableRow[] = [...receiptRows, ...groupedRows];

  return (
    <>
      <ImsTable headCells={headCells} rows={rows} verticalAlign="top" />

      {receiptDialogVisible && (
        <ReceiptDialog
          interaction={receiptInteraction}
          receiptDetails={receiptDetails}
          setReceiptDialogVisible={setReceiptDialogVisible}
        />
      )}
      {noteDialogVisible && <NoteDialog note={note} setNoteDialogVisible={setNoteDialogVisible} />}
    </>
  );
};

function GroupedRewards({ row }: Readonly<{ row: GroupedHistory }>) {
  const { t } = useTranslation();
  const earnedParts = [row.action, row.customer].filter(Boolean);
  const redeemedParts = [row.bonus, row.reversal].filter(Boolean);

  const hasEarned = earnedParts.some(
    (part) => Number(part!.interaction?.bonusPoints) || Number(part!.interaction?.stampCard)
  );
  const hasRedeemed = redeemedParts.some(
    (part) => Number(part!.interaction?.bonusPoints) || Number(part!.interaction?.stampCard)
  );

  if (!hasEarned && !hasRedeemed) return <div />;

  const needsLabels = hasEarned && hasRedeemed;

  return (
    <div>
      {hasEarned && (
        <div>
          {needsLabels && (
            <div style={{ fontSize: "0.75rem", color: "#888", marginBottom: 2 }}>
              {t("historyFeed.reward.earned")}
            </div>
          )}
          {earnedParts.map((part) => (
            <span key={part!.historyNumber}>{formatRewards(part!.interaction?.bonusPoints, part!.interaction?.stampCard)}</span>
          ))}
        </div>
      )}
      {hasEarned && hasRedeemed && <div style={{ margin: "4px 0" }} />}
      {hasRedeemed && (
        <div>
          {needsLabels && (
            <div style={{ fontSize: "0.75rem", color: "#888", marginBottom: 2 }}>
              {t("historyFeed.reward.redeemed")}
            </div>
          )}
          {redeemedParts.map((part) => (
            <span key={part!.historyNumber}>{formatRewards(part!.interaction?.bonusPoints, part!.interaction?.stampCard)}</span>
          ))}
        </div>
      )}
    </div>
  );
}

function formatAmount(element: CustomerHistoryDto | undefined) {
  if (!element) return null;
  const amount =
    element.interaction?.paymentAmount ||
    element.interaction?.reversalAmount ||
    element.interaction?.cashValue ||
    element.interaction?.reversalCashValue;
  if (!amount) return <br />;
  const negativeAmount =
    element.interaction?.reversalAmount || element.interaction?.cashValue;
  const value = Number(amount) * (Boolean(negativeAmount) ? -1 : 1);
  return (
    <>
      {formatEuro(value)}
      <br />
    </>
  );
}

function groupHistory(customerHistory: CustomerHistoryDto[]): GroupedHistory[] {
  const groups: GroupedHistory[] = [];

  customerHistory.forEach((history) => {
    // Skip coupon-related history entries
    if (history.entityType === EntityType.COUPON) {
      return;
    }

    const id = history.referenceId;
    let i = groups.findIndex(
      (group) =>
        id &&
        (id === group.action?.referenceId ||
          id === group.reversal?.referenceId ||
          id === group.bonus?.referenceId ||
          id === group.customer?.referenceId)
    );
    if (i === -1) {
      i = groups.push({}) - 1;
    }
    if (
      history.interaction?.interactionType === InteractionType.BONUS_XPRESS_INTERACTION ||
      history.interaction?.interactionType === InteractionType.BONUS_XPRESS_REVERSAL_INTERACTION
    ) {
      groups[i].bonus = history;
    } else if (history.interaction?.interactionType === InteractionType.REVERSAL_INTERACTION) {
      groups[i].reversal = history;
    } else if (history.entityType === EntityType.CUSTOMER) {
      groups[i].customer = history;
    } else {
      groups[i].action = history;
    }
  });
  return groups;
}

function formatEntityType(
  row: CustomerHistoryDto,
  operationalUnits: OperationalUnitDto[],
  suppressInlineUnit?: boolean
): ReactNode {
  const t = i18n.t;
  switch (row.entityType) {
    case EntityType.ADDRESS:
      return t("historyFeed.entityType.address");
    case EntityType.CUSTOMER:
      return t("historyFeed.entityType.customer");
    case EntityType.DSGVO:
      return t("historyFeed.entityType.dsgvo");
    case EntityType.CUSTOMER_STATUS:
      return t("historyFeed.entityType.customerStatus");
    case EntityType.PREFERENCE:
      return t("historyFeed.entityType.preference");
    case EntityType.CUSTOMER_IDENTIFICATION:
      return row.eventType === EventType.REPLACEMENT
        ? t("historyFeed.entityType.replacementCard")
        : t("historyFeed.entityType.card");
    case EntityType.CIRCLE:
      return t("historyFeed.entityType.circle");
    case EntityType.INTERACTION:
      const operationalUnit = operationalUnits.find(
        (unit) => unit.externalUnitNumber === row.interaction?.externalUnitNumber
      );
      const interaction = row.interaction!;
      switch (interaction.interactionType) {
        case InteractionType.CUSTOMER_INTERACTION:
          return formatCustomerInteractionType(interaction.customerInteractionType!, t);
        case InteractionType.PAYMENT_INTERACTION:
          return formatOperationalUnitLink(t("historyFeed.interaction.purchase"), operationalUnit, suppressInlineUnit);
        case InteractionType.REVERSAL_INTERACTION:
          return formatOperationalUnitLink(
            t("historyFeed.interaction.purchaseReversal"),
            operationalUnit,
            suppressInlineUnit
          );
        case InteractionType.BONUS_XPRESS_REVERSAL_INTERACTION:
          return t("historyFeed.interaction.bonusXpressReversal");
        case InteractionType.BONUS_XPRESS_INTERACTION:
          return t("historyFeed.interaction.bonusXpress");
        case InteractionType.ADJUSTMENT_EARN_INTERACTION:
          return t("historyFeed.interaction.goodwillCredit");
        case InteractionType.ADJUSTMENT_BURN_INTERACTION:
          return t("historyFeed.interaction.debit");
        default:
          return interaction.interactionType;
      }
    case EntityType.RECEIPT:
      if (row.eventType === EventType.UPDATE && row.receipt) {
        const status = row.receipt.status;
        switch (status) {
          case ReceiptStatus.REQUESTING_CREATE_INTERACTION:
            return t("historyFeed.entityType.receiptCalculating");
          case ReceiptStatus.COMPLETED:
            return t("historyFeed.entityType.receiptCompleted", { bonusPoint: row.receipt.bonusPoints ?? 0 });
          case ReceiptStatus.APPROVED:
            return t("historyFeed.entityType.receiptApproved");
          case ReceiptStatus.REJECTED:
            return t("historyFeed.entityType.receiptRejected");
          case ReceiptStatus.FAILED:
          case 'UPLOAD_FAILED' as ReceiptStatus:
          case 'EXTRACT_FAILED' as ReceiptStatus:
            return t("historyFeed.entityType.receiptFailed");
          case 'WAITING_FOR_APPROVAL' as ReceiptStatus:
            return t("historyFeed.entityType.receiptWaitingApproval");
          case ReceiptStatus.UPLOADED:
            return t("historyFeed.entityType.receiptUploaded");
          default:
            return t("historyFeed.entityType.receiptUnknown");
        }
      }
      if (row.eventType === EventType.CREATE && row.receipt?.status === ReceiptStatus.UPLOADING) {
        return t("historyFeed.entityType.receiptUploading");
      }
  }
}

const formatEventType = (row: CustomerHistoryDto): ReactNode => {
  const t = i18n.t;
  if (row.entityType === EntityType.CUSTOMER_STATUS) {
    const executionMessage = row.executionDate
      ? ` ${t("historyFeed.eventType.on")} ${formatGermanDate(row.executionDate)}`
      : "";
    return `${t("historyFeed.eventType.textChanged")}: ${getCustomerStatusText(
      row.customerStatus!,
      t
    )} ${executionMessage}`;
  }
  if (
    row.entityType !== EntityType.INTERACTION &&
    row.entityType !== EntityType.DSGVO &&
    row.entityType !== EntityType.RECEIPT
  )
    switch (row.eventType) {
      case EventType.CREATE:
        return t("historyFeed.eventType.create");
      case EventType.UPDATE:
        return t("historyFeed.eventType.update");
      case EventType.DELETE:
        return t("historyFeed.eventType.delete");
      case EventType.REPLACEMENT:
        return t("historyFeed.eventType.replacement");
      case EventType.PRODUCE:
        return t("historyFeed.eventType.produce");
    }
};

const formatEventDetails = (row: CustomerHistoryDto): ReactNode => {
  const t = i18n.t;
  if (!row.eventDetails) {
    return;
  }
  if (row.eventDetails.creationDetails) {
    return row.eventDetails.creationDetails.details;
  }
  if (row.eventDetails.updateDetails) {
    if (row.eventType === EventType.TRANSFER_OWNERSHIP) {
      if (row.eventDetails.updateDetails.to === TransferType.REMOVED) {
        return t("historyFeed.eventDetails.removed", { from: row.eventDetails.updateDetails.from });
      }
      return t("historyFeed.eventDetails.added", { to: row.eventDetails.updateDetails.to });
    }
    return t("historyFeed.eventDetails.fromTo", {
      from: row.eventDetails.updateDetails.from,
      to: row.eventDetails.updateDetails.to,
    });
  }
};

function formatOperationalUnitLink(
  title: string,
  operationalUnit?: OperationalUnitDto,
  suppressInlineUnit?: boolean
): ReactNode {
  return (
    <>
      {title}
      {operationalUnit && !suppressInlineUnit && (
        <>
          {" | "} {operationalUnit.unitName}
        </>
      )}
    </>
  );
}

const mapStateToProps = (state: IStore) => ({
  customerHistory: state.customerCare.customerHistory,
  customerNumber: state.customerCare.customer?.customerNumber,
  operationalUnits: state.operationalUnits.operationalUnits,
  receiptDetails: state.customerCare.receiptDetails,
});

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators({ thunkFindReceiptDetailsForInteraction }, dispatch);

const connector = connect(mapStateToProps, mapDispatchToProps);
type ThunkProps = ConnectedProps<typeof connector>;
export default connector(HistoryFeedTable);
